import { useFeatureGate } from '@statsig/expo-bindings';
import { Redirect, router, useLocalSearchParams } from 'expo-router';
import { useState } from 'react';
import {
    BodyMediumRegular,
    Button,
    ButtonVariant,
    FullScreenSpinner,
    Icon,
    SafeAreaView,
    Sheet,
    SquarePenIcon,
    View,
    YStack,
} from 'smoothie';

import { useViewEvents } from '../../../events/hooks/useViewEvents';
import { FeatureFlags } from '../../../feature-flags/FeatureFlags';
import { useMembershipMoneyMaps } from '../../hooks/useMembershipMoneyMaps';
import { canMemberEditMoneyMap } from '../../utils/moneyMapMemberEditEligibility';
import { RoadmapScreenHeader } from '../RoadmapScreenHeader';
import MoneyMap from './MoneyMap';
import { MoneyMapRedesign } from './MoneyMapRedesign';

export const ViewMoneyMap = () => {
    const { trackViewOnce } = useViewEvents();
    const { value: enableMoneySystemTab } = useFeatureGate(
        FeatureFlags.ENABLE_MONEY_SYSTEM_TAB,
    );
    const [isEditActive, setIsEditActive] = useState(false);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [hasValidationErrors, setHasValidationErrors] = useState(false);
    const [showConfirmExit, setShowConfirmExit] = useState(false);

    const { moneyMapId: moneyMapIdParam } = useLocalSearchParams<{
        moneyMapId?: string;
    }>();
    const moneyMapId =
        typeof moneyMapIdParam === 'string'
            ? moneyMapIdParam
            : Array.isArray(moneyMapIdParam) &&
                typeof moneyMapIdParam[0] === 'string'
              ? moneyMapIdParam[0]
              : undefined;

    const {
        workflowMoneyMap,
        liveSplitMoneyMap,
        currentIncomeSplit,
        findLoadedMoneyMap,
        isLoading,
        hasPreviousMoneyMaps,
    } = useMembershipMoneyMaps();

    const moneyMap =
        moneyMapId != null && moneyMapId.length > 0
            ? findLoadedMoneyMap(moneyMapId)
            : (liveSplitMoneyMap ?? workflowMoneyMap);

    if (moneyMap == null && isLoading) {
        return <FullScreenSpinner />;
    }

    if (moneyMap == null) {
        return <Redirect href="/home/roadmap" />;
    }

    trackViewOnce('Roadmap/MoneyMap');

    const canEdit = canMemberEditMoneyMap(moneyMap, currentIncomeSplit);

    if (!enableMoneySystemTab) {
        return (
            <SafeAreaView edges={['left', 'right', 'bottom']}>
                <RoadmapScreenHeader
                    title="Money Map"
                    hasBackButton={true}
                    hasMessagingButton={true}
                />
                <MoneyMap moneyMap={moneyMap} />
            </SafeAreaView>
        );
    }

    const editButton = isEditActive ? (
        <View
            onPress={
                hasValidationErrors
                    ? undefined
                    : () => {
                          setIsConfirmOpen(true);
                      }
            }
            paddingVertical={8}
            paddingHorizontal={16}
            borderRadius={16}
            backgroundColor="$gray200"
            cursor={hasValidationErrors ? 'not-allowed' : 'pointer'}
            opacity={hasValidationErrors ? 0.5 : 1}
            alignItems="center"
            justifyContent="center"
            animation="button"
            animateOnly={['transform', 'backgroundColor']}
            pressStyle={hasValidationErrors ? undefined : { scale: 0.95 }}
        >
            <BodyMediumRegular>Save</BodyMediumRegular>
        </View>
    ) : (
        <View
            onPress={() => {
                setIsEditActive(true);
            }}
            padding={8}
            borderRadius={16}
            backgroundColor="$gray200"
            cursor="pointer"
            alignItems="center"
            justifyContent="center"
            animation="button"
            animateOnly={['transform', 'backgroundColor']}
            pressStyle={{ scale: 0.95 }}
        >
            <Icon icon={SquarePenIcon} size={20} color="$gray1200" />
        </View>
    );

    return (
        <SafeAreaView edges={['left', 'right', 'bottom']}>
            <RoadmapScreenHeader
                title="Money Map"
                hasBackButton={true}
                onBack={() => {
                    if (isEditActive) {
                        setShowConfirmExit(true);
                    } else {
                        if (router.canGoBack()) {
                            router.back();
                        } else {
                            router.navigate({
                                pathname: '/home/roadmap',
                            });
                        }
                    }
                }}
                rightContent={canEdit ? editButton : undefined}
            />
            <MoneyMapRedesign
                moneyMap={moneyMap}
                mode={isEditActive ? 'edit-mode-active' : 'edit-mode'}
                confirmSheetOpen={isConfirmOpen}
                onConfirmSheetClose={() => {
                    setIsConfirmOpen(false);
                }}
                onConfirmDone={() => {
                    setIsEditActive(false);
                }}
                onValidationChange={setHasValidationErrors}
                hasPreviousMoneyMaps={hasPreviousMoneyMaps}
            />
            <Sheet
                isOpen={showConfirmExit}
                onClose={() => {
                    setShowConfirmExit(false);
                }}
                title="Leave without saving?"
                showCloseButton={false}
            >
                <YStack gap={24}>
                    <BodyMediumRegular>
                        Edits to your Money Map haven&#39;t been saved.
                    </BodyMediumRegular>
                    <YStack gap={16}>
                        <Button
                            text="Keep editing"
                            variant={ButtonVariant.PRIMARY}
                            onPress={() => {
                                setShowConfirmExit(false);
                            }}
                        />
                        <Button
                            text="Discard changes"
                            variant={ButtonVariant.SECONDARY}
                            onPress={() => {
                                setShowConfirmExit(false);
                                setIsEditActive(false);
                                if (router.canGoBack()) {
                                    router.back();
                                } else {
                                    router.navigate({
                                        pathname: '/home/roadmap',
                                    });
                                }
                            }}
                        />
                    </YStack>
                </YStack>
            </Sheet>
        </SafeAreaView>
    );
};
